import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, Plus, Search, Mail, Phone, Euro, FileText, Key, Send, Clock,
  CheckCircle, XCircle, Loader2, Copy, Eye, Trash2, History,
  Download, RefreshCw, Shield, Calendar, ArrowRight, Receipt,
  UserPlus, Headphones, Wrench, Award, Building2, CreditCard, Landmark,
  ExternalLink, CalendarCheck,
  ClipboardCheck, Target,
} from 'lucide-react';
import api from '@/lib/api';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import AuditVenteTab from '@/components/AuditVenteTab';
import ConfigurateurProspect from '@/components/ConfigurateurProspect';
import { ClosingTab, OnboardingTab } from '@/components/ClosingOnboardingTabs';
import GuideVenteTab from '@/components/GuideVenteTab';
import BookingManagementTab from '@/components/BookingManagementTab';

const STATUTS = {
  nouveau: { label: 'Nouveau', color: 'bg-blue-100 text-blue-800', icon: UserPlus },
  en_cours: { label: 'En cours', color: 'bg-amber-100 text-amber-800', icon: Clock },
  devis_envoye: { label: 'Devis envoyé', color: 'bg-orange-100 text-orange-800', icon: FileText },
  essai: { label: 'En essai', color: 'bg-purple-100 text-purple-800', icon: Key },
  essai_expire: { label: 'Essai expiré', color: 'bg-red-100 text-red-800', icon: Clock },
  gagne: { label: 'Client actif', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  perdu: { label: 'Perdu', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const PALIERS = {
  petite: { nom: 'Petite Exploitation', prix: '100-150', defaut: 125, color: 'border-blue-400 bg-blue-50', accent: 'text-blue-700' },
  moyenne: { nom: 'Moyenne Exploitation', prix: '500', defaut: 500, color: 'border-amber-400 bg-amber-50', accent: 'text-amber-700' },
  grosse: { nom: 'Grosse Exploitation', prix: '1200', defaut: 1200, color: 'border-emerald-400 bg-emerald-50', accent: 'text-emerald-700' },
};

const SOURCES = {
  direct: 'Contact direct',
  ambassadeur: 'Ambassadeur / Parrain',
  bouche_a_oreille: 'Bouche à oreille',
  salon: 'Salon / Événement',
  web: 'Site web',
};

export default function CRMVenteLogicielPage() {
  const { activeSociete } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [prospects, setProspects] = useState([]);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [activeTab, setActiveTab] = useState('prospects');

  const [showNewProspect, setShowNewProspect] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showGenerateAccess, setShowGenerateAccess] = useState(false);
  const [showConfigurator, setShowConfigurator] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingAccess, setGeneratingAccess] = useState(false);
  const [payingFacture, setPayingFacture] = useState(null);
  const [deletingProspect, setDeletingProspect] = useState(null);

  const [factures, setFactures] = useState([]);
  const [commissions, setCommissions] = useState(null);

  const [newProspect, setNewProspect] = useState({
    nom_entreprise: '', contact_nom: '', contact_prenom: '', contact_email: '',
    contact_telephone: '', ville: '', surface_ha: '', source: 'direct',
    palier: 'petite', ambassadeur_email: '', ambassadeur_nom: '', notes: '',
  });

  useEffect(() => { fetchProspects(); }, []);

  const fetchProspects = async () => {
    try {
      const res = await api.get('/crm-vente-logiciel/prospects');
      setProspects(res.data?.prospects || []);
    } catch (e) {
      console.error('Erreur chargement prospects:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchFactures = async () => {
    try {
      const res = await api.get('/crm-vente-logiciel/factures');
      setFactures(res.data?.factures || []);
    } catch (e) { console.error(e); }
  };

  const fetchCommissions = async () => {
    try {
      const res = await api.get('/crm-vente-logiciel/commissions');
      setCommissions(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (activeTab === 'factures') fetchFactures();
    if (activeTab === 'commissions') fetchCommissions();
  }, [activeTab]);

  // Gestion retour Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const sessionId = params.get('session_id');
    const factureId = params.get('facture_id');

    if (payment === 'success' && sessionId && factureId) {
      setActiveTab('factures');
      pollPaymentStatus(factureId, sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (payment === 'canceled') {
      toast.info('Paiement annulé');
      setActiveTab('factures');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (factureId, sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info('Vérification du paiement en cours... Veuillez rafraîchir dans un instant.');
      return;
    }
    try {
      const res = await api.get(`/crm-vente-logiciel/factures/${factureId}/checkout-status/${sessionId}`);
      if (res.data?.payment_status === 'paid' || res.data?.facture_payee) {
        toast.success('Paiement reçu ! Facture marquée comme payée.');
        fetchFactures();
        return;
      }
      if (res.data?.status === 'expired') {
        toast.error('Session de paiement expirée.');
        return;
      }
      setTimeout(() => pollPaymentStatus(factureId, sessionId, attempts + 1), 2000);
    } catch (e) {
      console.error('Poll error:', e);
      if (attempts < 4) setTimeout(() => pollPaymentStatus(factureId, sessionId, attempts + 1), 2000);
    }
  };

  const handleStripeCheckout = async (facture) => {
    setPayingFacture(facture.id);
    try {
      const originUrl = window.location.origin;
      const res = await api.post(`/crm-vente-logiciel/factures/${facture.id}/checkout`, { origin_url: originUrl });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Erreur: pas de lien de paiement');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur paiement Stripe');
    } finally {
      setPayingFacture(null);
    }
  };

  const handleMarquerVirement = async (facture) => {
    setSaving(true);
    try {
      await api.put(`/crm-vente-logiciel/factures/${facture.id}/payer-virement`);
      toast.success('Facture marquée payée par virement');
      fetchFactures();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateProspect = async () => {
    if (!newProspect.nom_entreprise || !newProspect.contact_email) {
      toast.error('Nom entreprise et email requis');
      return;
    }
    setSaving(true);
    try {
      const palier = PALIERS[newProspect.palier] || PALIERS.petite;
      const res = await api.post('/crm-vente-logiciel/creer-prospect', {
        ...newProspect,
        surface_ha: parseInt(newProspect.surface_ha) || 0,
        activites: [],
        nb_workspaces: 1,
        nb_collaborateurs: 1,
        options: [],
        type_paiement: 'mensuel',
        prix_mensuel_propose: palier.defaut,
      });
      if (res.data?.success) {
        toast.success('Prospect créé !');
        setShowNewProspect(false);
        setNewProspect({
          nom_entreprise: '', contact_nom: '', contact_prenom: '', contact_email: '',
          contact_telephone: '', ville: '', surface_ha: '', source: 'direct',
          palier: 'petite', ambassadeur_email: '', ambassadeur_nom: '', notes: '',
        });
        fetchProspects();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    } finally {
      setSaving(false);
    }
  };

  const handleSendDevis = async (prospect) => {
    setSaving(true);
    try {
      const res = await api.post(`/crm-vente-logiciel/prospects/${prospect.id}/generer-envoyer-devis`);
      if (res.data?.success) {
        toast.success(`Devis ${res.data.numero} envoyé à ${prospect.contact_email}`);
        fetchProspects();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi devis');
    } finally {
      setSaving(false);
    }
  };

  const handleSendCredentials = async (prospect) => {
    setSaving(true);
    try {
      const res = await api.post(`/crm-vente-logiciel/prospects/${prospect.id}/envoyer-credentials`);
      if (res.data?.success) {
        toast.success('Identifiants envoyés par email !');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAccess = async (temporaire = false, dureeJours = null) => {
    if (!selectedProspect) return;
    setGeneratingAccess(true);
    try {
      const res = await api.post(`/crm-vente-logiciel/prospects/${selectedProspect.id}/generer-acces`, {
        temporaire, duree_essai_jours: temporaire ? dureeJours : null
      });
      if (res.data?.success) {
        toast.success(temporaire ? `Accès essai ${dureeJours}j généré !` : 'Accès définitif généré !');
        setShowGenerateAccess(false);
        fetchProspects();
        if (res.data.credentials) {
          // Auto-envoyer les credentials par email
          try {
            await api.post(`/crm-vente-logiciel/prospects/${selectedProspect.id}/envoyer-credentials`);
            toast.success('Identifiants envoyés par email automatiquement !');
          } catch (emailErr) {
            console.warn('Email auto-send failed:', emailErr);
          }
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur génération accès');
    } finally {
      setGeneratingAccess(false);
    }
  };

  const handleCreateFacture = async (prospect) => {
    const prix = prospect.prix_mensuel_valide || prospect.prix_mensuel_propose || 0;
    if (!prix) { toast.error('Pas de prix défini'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/crm-vente-logiciel/factures?prospect_id=${prospect.id}&montant=${prix}&description=Abonnement Bureau Agricole - ${prospect.nom_entreprise}`);
      if (res.data?.success) {
        toast.success(`Facture ${res.data.numero} créée et envoyée !`);
        fetchFactures();
        fetchProspects();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur facturation');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoRepair = async () => {
    setSaving(true);
    try {
      const res = await api.post('/crm-vente-logiciel/auto-reparation');
      if (res.data?.total > 0) {
        toast.success(`${res.data.total} réparation(s) effectuée(s)`);
      } else {
        toast.info('Aucun problème détecté');
      }
      fetchProspects();
    } catch (e) {
      toast.error('Erreur auto-réparation');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProspect = async (prospect) => {
    if (!window.confirm(`Supprimer définitivement "${prospect.nom_entreprise}" et toutes ses données ?`)) return;
    setDeletingProspect(prospect.id);
    try {
      const res = await api.delete(`/crm-vente-logiciel/prospects/${prospect.id}`);
      if (res.data?.success) {
        toast.success(res.data.message || 'Prospect supprimé');
        setShowDetail(false);
        setSelectedProspect(null);
        fetchProspects();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    } finally {
      setDeletingProspect(null);
    }
  };

  const copyCredentials = (creds) => {
    if (!creds) return;
    navigator.clipboard.writeText(`URL: ${creds.url_connexion}\nEmail: ${creds.email}\nMot de passe: ${creds.mot_de_passe}`);
    toast.success('Copié !');
  };

  const filteredProspects = prospects.filter(p => {
    const matchSearch = !searchTerm ||
      p.nom_entreprise?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.contact_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.contact_nom?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatut = filterStatut === 'tous' || p.statut === filterStatut;
    return matchSearch && matchStatut;
  });

  const stats = {
    total: prospects.length,
    nouveaux: prospects.filter(p => p.statut === 'nouveau').length,
    enEssai: prospects.filter(p => p.statut === 'essai').length,
    clients: prospects.filter(p => p.statut === 'gagne').length,
    mrr: prospects.filter(p => p.statut === 'gagne').reduce((acc, p) => acc + (p.prix_mensuel_valide || p.prix_mensuel_propose || 0), 0),
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="crm-vente-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="crm-title">
            <Building2 className="w-8 h-8 text-emerald-700" />
            Vente Bureau Agricole
          </h1>
          <p className="text-muted-foreground">Pipeline commercial complet</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleAutoRepair} disabled={saving} data-testid="btn-auto-repair">
            <Wrench className="w-4 h-4 mr-2" />Auto-réparation
          </Button>
          <Button variant="outline" onClick={() => setShowConfigurator(true)} data-testid="btn-configurateur">
            <Wrench className="w-4 h-4 mr-2" />Configurateur
          </Button>
          <Button variant="outline" onClick={async () => {
            try {
              const res = await api.post('/booking/creer-lien', {
                titre: 'Prise de RDV Bureau Agricole',
                description: 'Réservez un créneau pour une démonstration ou un échange avec Elsa.',
                canaux: ['visio', 'telephone', 'presentiel'],
                duree_defaut: 30,
              });
              if (res.data?.success) {
                const url = `${window.location.origin}/booking/${res.data.link_id}`;
                navigator.clipboard.writeText(url).then(() => toast.success('Lien copié !')).catch(() => {});
                toast.success(`Lien créé : ${url}`);
              }
            } catch (e) { toast.error('Erreur création lien'); }
          }} data-testid="btn-creer-lien-rdv">
            <Calendar className="w-4 h-4 mr-2" />Lien RDV
          </Button>
          <Button onClick={() => setShowNewProspect(true)} data-testid="btn-new-prospect">
            <Plus className="w-4 h-4 mr-2" />Nouveau Prospect
          </Button>
          <Button
            variant="default"
            className="bg-red-600 hover:bg-red-700"
            onClick={() => { window.location.href = '/espace-commercial-visio'; }}
            data-testid="btn-visio-live"
          >
            🎥 Visio live
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        {[
          { label: 'Total', value: stats.total, icon: Users, color: '' },
          { label: 'Nouveaux', value: stats.nouveaux, icon: UserPlus, color: 'text-blue-600' },
          { label: 'En essai', value: stats.enEssai, icon: Key, color: 'text-purple-600' },
          { label: 'Clients', value: stats.clients, icon: CheckCircle, color: 'text-green-600' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1"><s.icon className="w-4 h-4" /><span className="text-sm">{s.label}</span></div>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-green-700 mb-1"><Euro className="w-4 h-4" /><span className="text-sm">MRR</span></div>
            <p className="text-2xl font-bold text-green-700">{stats.mrr.toLocaleString()}€</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="prospects" data-testid="tab-prospects">Prospects</TabsTrigger>
          <TabsTrigger value="factures" data-testid="tab-factures">Factures</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="tab-commissions">Commissions</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit-vente" className="gap-1">
            <ClipboardCheck className="w-3.5 h-3.5" />
            Audit & Vente
          </TabsTrigger>
          <TabsTrigger value="configurateur" data-testid="tab-configurateur" className="gap-1">
            <Wrench className="w-3.5 h-3.5" />
            Configurateur
          </TabsTrigger>
          <TabsTrigger value="closing" data-testid="tab-closing" className="gap-1">
            <Target className="w-3.5 h-3.5" />
            Closing IA
          </TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding" className="gap-1">
            <ClipboardCheck className="w-3.5 h-3.5" />
            Onboarding
          </TabsTrigger>
          <TabsTrigger value="guide" data-testid="tab-guide-vente" className="gap-1">
            <FileText className="w-3.5 h-3.5" />
            Guide de Vente
          </TabsTrigger>
          <TabsTrigger value="booking" data-testid="tab-booking" className="gap-1">
            <CalendarCheck className="w-3.5 h-3.5" />
            Agenda RDV
          </TabsTrigger>
        </TabsList>

        {/* ===== TAB PROSPECTS ===== */}
        <TabsContent value="prospects" className="space-y-4">
          <div className="flex gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-prospects" />
            </div>
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous les statuts</SelectItem>
                {Object.entries(STATUTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            {filteredProspects.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <Building2 className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-lg font-medium">Aucun prospect</p>
              </CardContent></Card>
            ) : filteredProspects.map(prospect => {
              const StatutIcon = STATUTS[prospect.statut]?.icon || Clock;
              const palier = PALIERS[prospect.palier] || PALIERS.petite;
              return (
                <Card key={prospect.id} className="hover:shadow-md transition-shadow" data-testid={`prospect-card-${prospect.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 cursor-pointer" onClick={() => { setSelectedProspect(prospect); setShowDetail(true); }}>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{prospect.nom_entreprise}</h3>
                          <Badge className={STATUTS[prospect.statut]?.color || 'bg-gray-100'}>
                            <StatutIcon className="w-3 h-3 mr-1" />{STATUTS[prospect.statut]?.label || prospect.statut}
                          </Badge>
                          <Badge variant="outline" className={palier.accent}>{palier.nom}</Badge>
                          {prospect.source === 'ambassadeur' && (
                            <Badge variant="outline" className="border-amber-400 text-amber-700"><Award className="w-3 h-3 mr-1" />Ambassadeur</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-5 gap-4 text-sm">
                          <div><p className="text-muted-foreground">Contact</p><p>{prospect.contact_prenom} {prospect.contact_nom}</p></div>
                          <div><p className="text-muted-foreground">Email</p><p className="truncate">{prospect.contact_email}</p></div>
                          <div><p className="text-muted-foreground">Source</p><p>{SOURCES[prospect.source] || prospect.source}</p></div>
                          <div><p className="text-muted-foreground">Prix/mois</p><p className="font-semibold text-green-600">{prospect.prix_mensuel_valide || prospect.prix_mensuel_propose || '-'}€</p></div>
                          <div><p className="text-muted-foreground">Créé le</p><p>{new Date(prospect.created_at).toLocaleDateString('fr-FR')}</p></div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleSendDevis(prospect); }} disabled={saving} data-testid={`btn-devis-${prospect.id}`}>
                          <FileText className="w-4 h-4 mr-1" />Devis
                        </Button>
                        {!prospect.acces_genere ? (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={(e) => { e.stopPropagation(); setSelectedProspect(prospect); setShowGenerateAccess(true); }} data-testid={`btn-acces-${prospect.id}`}>
                            <Key className="w-4 h-4 mr-1" />Accès
                          </Button>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleSendCredentials(prospect); }} disabled={saving}>
                              <Send className="w-4 h-4 mr-1" />Renvoyer
                            </Button>
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleCreateFacture(prospect); }} disabled={saving} data-testid={`btn-facture-${prospect.id}`}>
                              <Receipt className="w-4 h-4 mr-1" />Facturer
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => { e.stopPropagation(); handleDeleteProspect(prospect); }}
                          disabled={deletingProspect === prospect.id}
                          data-testid={`btn-delete-${prospect.id}`}
                        >
                          {deletingProspect === prospect.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* ===== TAB FACTURES ===== */}
        <TabsContent value="factures" className="space-y-4">
          {factures.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <Receipt className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium">Aucune facture</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {factures.map(f => (
                <Card key={f.id} data-testid={`facture-${f.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Receipt className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-semibold">{f.numero}</p>
                          <p className="text-sm text-muted-foreground">{f.client_nom} - {f.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-lg">{f.montant_ttc}€ TTC</p>
                          <p className="text-xs text-muted-foreground">{f.montant_ht}€ HT + {f.tva}€ TVA</p>
                        </div>
                        <Badge className={f.statut === 'payee' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                          {f.statut === 'payee' ? (
                            <><CheckCircle className="w-3 h-3 mr-1" />Payée {f.methode_paiement === 'virement' ? '(Virement)' : f.methode_paiement === 'carte_stripe' ? '(Carte)' : ''}</>
                          ) : 'En attente'}
                        </Badge>
                        {f.statut !== 'payee' && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              className="bg-indigo-600 hover:bg-indigo-700"
                              onClick={() => handleStripeCheckout(f)}
                              disabled={payingFacture === f.id || saving}
                              data-testid={`btn-payer-carte-${f.id}`}
                            >
                              {payingFacture === f.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CreditCard className="w-4 h-4 mr-1" />}
                              Carte
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMarquerVirement(f)}
                              disabled={saving}
                              data-testid={`btn-payer-virement-${f.id}`}
                            >
                              <Landmark className="w-4 h-4 mr-1" />Virement
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                try {
                                  await api.put(`/crm-vente-logiciel/factures/${f.id}/payer`);
                                  toast.success('Facture marquée payée');
                                  fetchFactures();
                                } catch (e) { toast.error('Erreur'); }
                              }}
                              data-testid={`btn-payer-manuel-${f.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />Manuel
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ===== TAB COMMISSIONS ===== */}
        <TabsContent value="commissions" className="space-y-4">
          {!commissions || commissions.commissions?.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <Award className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium">Aucune commission</p>
              <p className="text-muted-foreground">Les commissions apparaissent quand un prospect venu via ambassadeur devient client</p>
            </CardContent></Card>
          ) : (
            <>
              <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-700">Commissions mensuelles totales</p>
                      <p className="text-3xl font-bold text-amber-800">{commissions.total_mensuel}€/mois</p>
                    </div>
                    <div className="text-right text-sm text-amber-600">
                      <p>Taux : {commissions.taux_commission}%</p>
                      <p>Durée : {commissions.duree_mois} mois</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {commissions.commissions.map((c, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Award className="w-5 h-5 text-amber-600" />
                        <div>
                          <p className="font-semibold">{c.ambassadeur_nom || c.ambassadeur_email}</p>
                          <p className="text-sm text-muted-foreground">{c.ambassadeur_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-amber-700">{c.total_commission_mensuelle}€/mois</p>
                        <p className="text-xs text-muted-foreground">{c.total_commission_annuelle}€/an</p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      {c.clients.map((cl, j) => (
                        <div key={j} className="flex justify-between text-sm p-2 bg-muted/50 rounded">
                          <span>{cl.nom_entreprise}</span>
                          <span className="text-muted-foreground">{cl.prix_mensuel}€ → {cl.commission_mensuelle}€/mois</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </TabsContent>

        {/* Audit & Vente */}
        <TabsContent value="audit" className="space-y-6" data-testid="audit-vente-content">
          <AuditVenteTab />
        </TabsContent>

        {/* Configurateur */}
        <TabsContent value="configurateur" className="space-y-6" data-testid="configurateur-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-emerald-600" />
                Configurateur Sur-Mesure
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ConfigurateurProspect mode="tab" onSuccess={fetchProspects} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Closing IA */}
        <TabsContent value="closing" className="space-y-6" data-testid="closing-content">
          <ClosingTab />
        </TabsContent>

        {/* Onboarding */}
        <TabsContent value="onboarding" className="space-y-6" data-testid="onboarding-content">
          <OnboardingTab />
        </TabsContent>

        {/* Guide de Vente */}
        <TabsContent value="guide" className="space-y-6" data-testid="guide-vente-content">
          <GuideVenteTab />
        </TabsContent>

        {/* Agenda RDV / Booking */}
        <TabsContent value="booking" className="space-y-6" data-testid="booking-management-content">
          <BookingManagementTab />
        </TabsContent>
      </Tabs>

      {/* ===== DIALOG NOUVEAU PROSPECT ===== */}
      <Dialog open={showNewProspect} onOpenChange={setShowNewProspect}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" />Nouveau Prospect</DialogTitle>
            <DialogDescription>Créez un prospect et choisissez son palier tarifaire</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Palier tarifaire */}
            <div>
              <Label className="text-base font-semibold mb-3 block">Palier tarifaire</Label>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(PALIERS).map(([key, p]) => (
                  <div
                    key={key}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                      newProspect.palier === key ? `${p.color} border-2 ring-2 ring-offset-1 ring-current` : 'border-muted hover:border-muted-foreground/50'
                    }`}
                    onClick={() => setNewProspect(prev => ({ ...prev, palier: key }))}
                    data-testid={`palier-${key}`}
                  >
                    <p className={`font-bold text-lg ${p.accent}`}>{p.prix}€</p>
                    <p className="text-sm text-muted-foreground">/mois</p>
                    <p className="font-medium mt-1 text-sm">{p.nom}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Infos entreprise */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nom de l'entreprise *</Label>
                <Input value={newProspect.nom_entreprise} onChange={e => setNewProspect(p => ({ ...p, nom_entreprise: e.target.value }))} placeholder="Ex: EARL Les Vignes" className="mt-1" data-testid="input-nom-entreprise" />
              </div>
              <div>
                <Label>Prénom</Label>
                <Input value={newProspect.contact_prenom} onChange={e => setNewProspect(p => ({ ...p, contact_prenom: e.target.value }))} placeholder="Jean" className="mt-1" />
              </div>
              <div>
                <Label>Nom</Label>
                <Input value={newProspect.contact_nom} onChange={e => setNewProspect(p => ({ ...p, contact_nom: e.target.value }))} placeholder="Dupont" className="mt-1" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={newProspect.contact_email} onChange={e => setNewProspect(p => ({ ...p, contact_email: e.target.value }))} placeholder="contact@exploitation.fr" className="mt-1" data-testid="input-email" />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input value={newProspect.contact_telephone} onChange={e => setNewProspect(p => ({ ...p, contact_telephone: e.target.value }))} placeholder="06 12 34 56 78" className="mt-1" />
              </div>
              <div>
                <Label>Ville</Label>
                <Input value={newProspect.ville} onChange={e => setNewProspect(p => ({ ...p, ville: e.target.value }))} placeholder="Lyon" className="mt-1" />
              </div>
              <div>
                <Label>Surface (ha)</Label>
                <Input type="number" value={newProspect.surface_ha} onChange={e => setNewProspect(p => ({ ...p, surface_ha: e.target.value }))} placeholder="50" className="mt-1" />
              </div>
            </div>

            {/* Source */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Source du prospect</Label>
                <Select value={newProspect.source} onValueChange={v => setNewProspect(p => ({ ...p, source: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SOURCES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {newProspect.source === 'ambassadeur' && (
                <>
                  <div>
                    <Label>Nom ambassadeur</Label>
                    <Input value={newProspect.ambassadeur_nom} onChange={e => setNewProspect(p => ({ ...p, ambassadeur_nom: e.target.value }))} placeholder="Marie Martin" className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label>Email ambassadeur</Label>
                    <Input type="email" value={newProspect.ambassadeur_email} onChange={e => setNewProspect(p => ({ ...p, ambassadeur_email: e.target.value }))} placeholder="ambassadeur@email.com" className="mt-1" data-testid="input-ambassadeur-email" />
                  </div>
                </>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={newProspect.notes} onChange={e => setNewProspect(p => ({ ...p, notes: e.target.value }))} placeholder="Informations complémentaires..." className="mt-1" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProspect(false)}>Annuler</Button>
            <Button onClick={handleCreateProspect} disabled={saving} data-testid="btn-create-prospect">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== DIALOG DÉTAIL PROSPECT ===== */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedProspect?.nom_entreprise}</DialogTitle>
          </DialogHeader>
          {selectedProspect && (
            <Tabs defaultValue="infos" className="mt-2">
              <TabsList className="grid grid-cols-3">
                <TabsTrigger value="infos">Infos</TabsTrigger>
                <TabsTrigger value="acces">Accès</TabsTrigger>
                <TabsTrigger value="historique">Historique</TabsTrigger>
              </TabsList>
              <TabsContent value="infos" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-muted-foreground">Contact</Label><p>{selectedProspect.contact_prenom} {selectedProspect.contact_nom}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Email</Label><p>{selectedProspect.contact_email}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Téléphone</Label><p>{selectedProspect.contact_telephone || '-'}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Ville</Label><p>{selectedProspect.ville || '-'}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Palier</Label><p className="font-semibold">{PALIERS[selectedProspect.palier]?.nom || selectedProspect.palier}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Abonnement</Label><p className="font-semibold text-green-600">{selectedProspect.prix_mensuel_valide || selectedProspect.prix_mensuel_propose}€/mois</p></div>
                  <div><Label className="text-xs text-muted-foreground">Source</Label><p>{SOURCES[selectedProspect.source] || selectedProspect.source}</p></div>
                  {selectedProspect.ambassadeur_nom && <div><Label className="text-xs text-muted-foreground">Ambassadeur</Label><p>{selectedProspect.ambassadeur_nom} ({selectedProspect.ambassadeur_email})</p></div>}
                </div>
                <div className="flex gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => handleSendDevis(selectedProspect)} disabled={saving}><FileText className="w-4 h-4 mr-2" />Envoyer devis</Button>
                  {selectedProspect.acces_genere && <Button variant="outline" onClick={() => handleCreateFacture(selectedProspect)} disabled={saving}><Receipt className="w-4 h-4 mr-2" />Facturer</Button>}
                  {!selectedProspect.acces_genere && <Button onClick={() => { setShowDetail(false); setShowGenerateAccess(true); }}><Key className="w-4 h-4 mr-2" />Générer accès</Button>}
                  <Button variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 ml-auto" onClick={() => handleDeleteProspect(selectedProspect)} disabled={deletingProspect === selectedProspect?.id} data-testid="btn-delete-detail">
                    <Trash2 className="w-4 h-4 mr-2" />Supprimer
                  </Button>
                </div>
              </TabsContent>
              <TabsContent value="acces" className="mt-4">
                {selectedProspect.credentials_client ? (
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg border ${selectedProspect.acces_verrouille ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                      <p className={`text-sm font-medium mb-3 ${selectedProspect.acces_verrouille ? 'text-red-700' : 'text-green-700'}`}>
                        {selectedProspect.acces_verrouille ? 'Accès verrouillé' : 'Accès actif'}
                      </p>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-muted-foreground">URL:</span> {selectedProspect.credentials_client.url_connexion}</p>
                        <p><span className="text-muted-foreground">Email:</span> {selectedProspect.credentials_client.email}</p>
                        <p><span className="text-muted-foreground">MDP:</span> <code className="bg-white p-1 rounded">{selectedProspect.credentials_client.mot_de_passe}</code></p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => copyCredentials(selectedProspect.credentials_client)}><Copy className="w-4 h-4 mr-2" />Copier</Button>
                      <Button variant="outline" onClick={() => handleSendCredentials(selectedProspect)} disabled={saving}><Send className="w-4 h-4 mr-2" />Renvoyer par email</Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Key className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Aucun accès généré</p>
                    <Button className="mt-4" onClick={() => { setShowDetail(false); setShowGenerateAccess(true); }}><Key className="w-4 h-4 mr-2" />Générer maintenant</Button>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="historique" className="mt-4">
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedProspect.historique?.map((h, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm p-2 bg-muted/50 rounded">
                      <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">{h.details || h.action}</p>
                        <p className="text-xs text-muted-foreground">{new Date(h.date).toLocaleString('fr-FR')}</p>
                      </div>
                    </div>
                  )) || <p className="text-center py-8 text-muted-foreground">Aucun historique</p>}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== DIALOG GENERER ACCES ===== */}
      <Dialog open={showGenerateAccess} onOpenChange={setShowGenerateAccess}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key className="w-5 h-5 text-green-600" />Accès - {selectedProspect?.nom_entreprise}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-muted-foreground">Les identifiants seront envoyés automatiquement par email.</p>
            {[
              { label: 'Essai 3 jours', desc: 'Démonstration rapide', icon: Clock, color: 'hover:border-blue-500', args: [true, 3] },
              { label: 'Accès définitif', desc: `Client actif - ${selectedProspect?.prix_mensuel_propose || ''}€/mois`, icon: CheckCircle, color: 'hover:border-green-500 bg-gradient-to-r from-green-50 to-emerald-50', args: [false, null] },
            ].map((opt, i) => (
              <Card key={i} className={`cursor-pointer hover:shadow-md transition-all ${opt.color}`} onClick={() => handleGenerateAccess(...opt.args)} data-testid={`btn-acces-${i}`}>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-4">
                    <opt.icon className="w-6 h-6 text-primary" />
                    <div className="flex-1">
                      <h4 className="font-semibold">{opt.label}</h4>
                      <p className="text-sm text-muted-foreground">{opt.desc}</p>
                    </div>
                    {generatingAccess && <Loader2 className="w-5 h-5 animate-spin" />}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== CONFIGURATEUR MODAL ===== */}
      <ConfigurateurProspect
        open={showConfigurator}
        onOpenChange={setShowConfigurator}
        onSuccess={fetchProspects}
        mode="modal"
      />
    </div>
  );
}
